<template>
  <el-tooltip
    :content="text"
    placement="top"
    :show-after="200"
    :hide-after="0"
    :disabled="!isOverflow"
  >
    <span
      ref="textRef"
      class="overflow-text"
      :class="{ 'text-overflow': isOverflow }"
      :style="{ maxWidth: maxWidth }"
    >
      <template v-if="$slots.default">
        <slot />
      </template>
      <template v-else>
        {{ text }}
      </template>
    </span>
  </el-tooltip>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, onUnmounted } from 'vue';

const props = defineProps({
  // 显示的文本内容
  text: {
    type: String,
    default: ''
  },
  // 最大宽度，可以是CSS值如 '200px', '100%' 等
  maxWidth: {
    type: String,
    default: '100%'
  },
  // 是否启用溢出检测
  enableOverflowCheck: {
    type: Boolean,
    default: true
  }
});

const textRef = ref(null);
const isOverflow = ref(false);

// 检测文本是否溢出
const checkOverflow = async () => {
  if (!props.enableOverflowCheck || !textRef.value) return;

  await nextTick();

  const element = textRef.value;
  if (!element) return;

  // 检查文本是否超出容器宽度
  isOverflow.value = element.scrollWidth > element.clientWidth;
};

// 监听文本变化
watch(() => props.text, () => {
  checkOverflow();
}, { immediate: true });

// 监听maxWidth变化
watch(() => props.maxWidth, () => {
  checkOverflow();
});

// 组件挂载后检测
onMounted(() => {
  checkOverflow();

  // 监听窗口大小变化，重新检测溢出
  window.addEventListener('resize', checkOverflow);
});

// 组件卸载时移除事件监听
onUnmounted(() => {
  window.removeEventListener('resize', checkOverflow);
});
</script>

<style scoped lang="scss">
.overflow-text {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;

  &.text-overflow {
    // 当文本溢出时，确保显示省略号
    text-overflow: ellipsis;
  }
}
</style>
